import { useCallback, useEffect, useRef, useState } from 'react';

// https://stackoverflow.com/questions/54954091/how-to-use-callback-with-usestate-hook-in-react
export function useStateCallback<T>(initialState: T): [T, (newState: T, cb?: (s: T) => void) => void] {
  const [state, setState] = useState(initialState);
  const cbRef = useRef(null); // mutable ref to store current callback

  const setStateCallback = (newState: T, cb?: (s: T) => void) => {
    cbRef.current = cb; // store passed callback to ref
    setState(newState);
  };

  useEffect(() => {
    // cb.current is `null` on initial render, so we only execute cb on state *updates*
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null; // reset callback after execution
    }
  }, [state]);

  return [state, setStateCallback];
}

// https://stackoverflow.com/questions/53446020/how-to-compare-oldvalues-and-newvalues-on-react-hooks-useeffect
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current ?? undefined;
};

export function useOpenState(initialValue: boolean = false) {
  const [
    isOpen,
    setIsOpen
  ] = useState(initialValue);

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  return {
    isOpen,
    onOpen,
    onClose,
    toggle
  };
}
